import { graphql, navigate } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import get from 'lodash.get';
import React, { useRef } from 'react';

import DXIThreeUp from '../components/Common/DXIThreeUp';
import Form from '../components/Common/DynamicForm/Form';
import { PrimaryLink } from '../components/Common/Links';
import MarketoForm from '../components/Common/MarketoForm/MarketoForm';
import PageSection from '../components/Common/PageSection';
import RichTextConverter from '../components/Common/RichText/RichTextConverter';
import { smoothScrollToRef } from '../components/Common/ScrollTo';
import Testimonial from '../components/DXI/Testimonial';
import Layout from '../components/Layouts/Layout';
import MarketingSeo from '../components/MarketingSeo';
import * as styles from './dxiOverview.module.css';

const DXIOverview = ({ data, location }) => {
  let page = {};
  if (data.page) page = data.page;

  const scrollRef = useRef();
  const onCtaClick = (e) => {
    e.preventDefault();
    if (!page.headingCtaScrollsToForm && page.headingCtaUrl) {
      return navigate(page.headingCtaUrl);
    }

    smoothScrollToRef(scrollRef, null, 84);
  };

  let sections = [];
  let usedForm = false;
  let pageSections = page.pageSections || [];
  pageSections.forEach((section, i) => {
    switch (section.__typename) {
      case 'ContentfulSimpleCard':
        sections.push(
          <PageSection key={i} className={styles.foam} curveTop={i === 0}>
            <div className={[styles.intro, styles.textSection].join(' ')}>
              <h3>{section.title}</h3>
              <RichTextConverter className={styles.boostExtend} copy={section.body} />
            </div>
          </PageSection>
        );
        break;

      case 'ContentfulContentCard':
        const isBlue = section.backgroundColor === 'Blue';
        const fiftySplit = section.imageStyle === 'Half & Half';
        const flipped = section.imageLocation;

        sections.push(
          <PageSection wide={fiftySplit} className={[styles.contentCard, isBlue && styles.foam].join(' ')}>
            <div className={[styles.twoColumns, fiftySplit && styles.fiftySplit, flipped && styles.flipped].join(' ')}>
              <div className={styles.copy}>
                <h3>{section.title}</h3>
                <RichTextConverter copy={section.copy} />
              </div>
              {section.mainImage.file.contentType !== 'image/svg+xml' ? (
                <GatsbyImage
                  alt={get(section, 'mainImage.alt') ? get(section, 'mainImage.alt') : ''}
                  image={get(section, 'mainImage.gatsbyImageData')}
                />
              ) : (
                <img alt={get(section, 'mainImage.alt')} src={get(section, 'mainImage.file.url')} />
              )}
            </div>
          </PageSection>
        );
        break;

      case 'ContentfulTestimonial':
        sections.push(
          <PageSection>
            <Testimonial
              pClassName={styles.boost}
              attribution={get(section, 'attribution')}
              body={get(section, 'quote.quote')}
              logo={get(section, 'logo')}
            />
          </PageSection>
        );
        break;

      case 'ContentfulMediaGroup':
        sections.push(
          <PageSection className={styles.alabaster}>
            <DXIThreeUp className={styles.margin} heading={section.title} mediaList={section.media} />
          </PageSection>
        );
        break;

      case 'ContentfulMarketoForm':
        if (usedForm) {
          break;
        }
        usedForm = true;
        sections.push(
          <>
            {page.headingCtaScrollsToForm && <div ref={scrollRef} />}
            <PageSection className={styles.gallery}>
              <div className={styles.formGrid}>
                <div className={styles.formCopy}>
                  <RichTextConverter copy={get(section, 'formCopy')} />
                </div>
                <div className={styles.form}>
                  {section.marketoForm && <Form form={section.marketoForm} programName={section.marketoProgramName} />}
                  {!section.marketoForm && <MarketoForm formId={section.marketoFormId} />}
                </div>
              </div>
            </PageSection>
          </>
        );
        break;

      default:
        break;
    }
  });

  return (
    <Layout>
      <MarketingSeo meta={page.pageMetaData} slug={location.pathname} />
      <PageSection isTopSection="true">
        <div className={styles.headingContainer}>
          <h1>{page.heading}</h1>
          <RichTextConverter copy={page.headingIntroCopy} />
          <PrimaryLink isButton label={page.headingCtaLabel} onClick={onCtaClick} />
        </div>
      </PageSection>
      {sections}
    </Layout>
  );
};

export default DXIOverview;

export const pageQuery = graphql`
  query DXIOverviewQuery($slug: String!) {
    page: contentfulMultipurposePageTemplate(slug: { eq: $slug }) {
      ...MultipurposePage
    }
  }
`;
